import React, { useCallback, useEffect, useRef, useState } from "react";
import type { CartItemsBlock } from "@/types/content";
import { useBodyClass } from "@/hooks/useBodyClass";
import { useCart } from "@/hooks/useCart";
import { useLocalizedTexts, t } from "@/utils/translations";
import { resolveHref } from "@/utils/url";
import { getLocale } from "@/utils/locale-store";
import { maskImeiCompact } from "@/utils/imei";
import {
  groupCartLines,
  isFixedQuantityProductType,
  isFreeLine,
} from "@/types/cart";
import { formatBillingPeriodLabel } from "@/utils/billingPeriod";
import { formatDate } from "@/utils/entitlements";
import { DiscountedUnitPrice } from "@/components/common/DiscountedUnitPrice/DiscountedUnitPrice";
import { isLineDiscounted } from "@/utils/discount";
import { formatVariantAttribute } from "@/utils/variant";
import { VARIANT_QUERY_PARAM } from "@/contexts/ProductContext";
import { VariantDropdown } from "@/components/common/VariantDropdown/VariantDropdown";
import { StockIndicator } from "@/components/common/StockIndicator/StockIndicator";
import type { CartLine } from "@/types/cart";
import { fixedCmsImage } from "@/utils/cmsImage";
import styles from "./CartItems.module.css";

export interface CartItemsProps {
  block: CartItemsBlock;
}

/**
 * Link back to the product page, reopened on the version that was bought so the
 * customer lands on the same size/colour they had picked.
 */
function productHref(
  productLink: NonNullable<CartLine["productLink"]>,
  variantReference: string | null,
  locale: string
): string {
  const href = resolveHref(productLink, locale);
  if (!variantReference) return href;
  const separator = href.includes("?") ? "&" : "?";
  return `${href}${separator}${VARIANT_QUERY_PARAM}=${encodeURIComponent(variantReference)}`;
}

export const CartItems: React.FC<CartItemsProps> = () => {
  useBodyClass("page--light");
  const {
    cart,
    isLoading,
    updateQuantity,
    removeLine,
    setLineVariant,
    unresolvedVariantLineIds,
    variantFocusRequest,
  } = useCart();
  const currentLocale = getLocale().language;
  const localizedTexts = useLocalizedTexts();

  // Per-line, not cart-wide: CartContext exposes a single isLoading, and using it
  // here would freeze every other row while one version is being saved.
  const [pendingVariantLineIds, setPendingVariantLineIds] = useState<
    Set<string>
  >(() => new Set());

  // The blocked checkout button lives in a sibling CMS block, so it asks through
  // the cart context and the first unresolved row answers here.
  const firstUnresolvedRef = useRef<HTMLSelectElement | null>(null);
  const firstUnresolvedLineId = unresolvedVariantLineIds[0] ?? null;

  useEffect(() => {
    // Nonce starts at 0 and only a real request bumps it, so the page never
    // scrolls on first render.
    if (variantFocusRequest === 0) return;
    const target = firstUnresolvedRef.current;
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.focus({ preventScroll: true });
  }, [variantFocusRequest]);

  const handleSelectVariant = useCallback(
    async (lineId: string, variantId: string) => {
      if (!variantId || pendingVariantLineIds.has(lineId)) return;

      setPendingVariantLineIds((prev) => new Set(prev).add(lineId));
      try {
        await setLineVariant(lineId, variantId);
      } finally {
        // Cleared even on failure so the customer can retry. On success the line
        // has been replaced and carries a new id, so this is a no-op.
        setPendingVariantLineIds((prev) => {
          if (!prev.has(lineId)) return prev;
          const next = new Set(prev);
          next.delete(lineId);
          return next;
        });
      }
    },
    [pendingVariantLineIds, setLineVariant]
  );

  // Track in-flight deletes per line so we can disable the button and show a
  // spinner. CartContext exposes a single `isLoading` for the whole cart, which
  // would block the user from interacting with other rows during one delete.
  const [pendingDeleteLineIds, setPendingDeleteLineIds] = useState<Set<string>>(
    () => new Set()
  );

  const handleRemoveLine = useCallback(
    async (lineId: string) => {
      // Idempotent guard: a re-entrant click while the previous request is
      // still in flight would otherwise fire a second DELETE that races with
      // the first one and can de-sync the cart on slow connections.
      if (pendingDeleteLineIds.has(lineId)) return;

      setPendingDeleteLineIds((prev) => {
        const next = new Set(prev);
        next.add(lineId);
        return next;
      });
      try {
        await removeLine(lineId);
      } finally {
        // Clear the flag even on failure so the user can retry. If the delete
        // succeeded the row is already gone, so the cleanup is a no-op.
        setPendingDeleteLineIds((prev) => {
          if (!prev.has(lineId)) return prev;
          const next = new Set(prev);
          next.delete(lineId);
          return next;
        });
      }
    },
    [pendingDeleteLineIds, removeLine]
  );

  const cartTitle = t(
    localizedTexts,
    "Tracker.Cart.YourShoppingCart",
    "Your shopping cart"
  );
  const decreaseLabel = t(
    localizedTexts,
    "Tracker.Generic.DecreaseQuantity",
    "Decrease quantity"
  );
  const increaseLabel = t(
    localizedTexts,
    "Tracker.Generic.IncreaseQuantity",
    "Increase quantity"
  );
  const removeLabel = t(localizedTexts, "Tracker.Generic.Remove", "Remove");
  const perPiece = t(localizedTexts, "Tracker.Cart.PerPiece", "/pcs");
  const freeLabel = t(localizedTexts, "Tracker.Cart.Free", "Free");
  const partOfBundleLabel = t(
    localizedTexts,
    "Tracker.Cart.PartOfBundle",
    "Part of {bundle}"
  );
  // A bundle is priced as a whole, so removing or re-quantifying one of its lines
  // applies to all of them server-side. Say so on the control rather than letting
  // the customer discover it after the click.
  const removeBundleLabel = t(
    localizedTexts,
    "Tracker.Cart.RemoveBundle",
    "Remove the whole bundle"
  );
  // Says what to do, not merely that something is wrong — the row is the only
  // place that knows which product is missing a version.
  const chooseVariantRequiredLabel = t(
    localizedTexts,
    "Tracker.Cart.ChooseVariantRequired",
    "Choose a size before continuing"
  );

  if (isLoading) {
    return (
      <div className={styles.container}>
        <h2 className={styles.title}>{cartTitle}</h2>
        <p className={styles.loading}>
          {t(localizedTexts, "Tracker.Cart.Loading", "Loading cart...")}
        </p>
      </div>
    );
  }

  if (!cart || cart.lines.length === 0) {
    return (
      <div className={styles.container}>
        <h2 className={styles.title}>{cartTitle}</h2>
        <p className={styles.empty}>
          {t(localizedTexts, "Tracker.Cart.Empty", "Your cart is empty.")}
        </p>
      </div>
    );
  }

  // Recurring carts expose a billing period (e.g. "/ year"); show it on the unit
  // price in place of "/pcs" so the cart matches the checkout. Null for physical.
  const periodLabel = formatBillingPeriodLabel(
    cart.billingPeriod,
    localizedTexts
  );
  // Only physical / one-time carts get the "/pcs" suffix. For a recurring cart
  // with no resolvable period label, show the bare unit price rather than
  // mislabelling a subscription as priced per piece.
  const isRecurring = cart.mode === "digitalRecurring";

  // B2B members see net (VAT 0%) prices labelled "Your price" beside the
  // "Recommended price" incl. VAT, plus an explicit "VAT 0%" tag.
  const isB2b = cart.isB2b;
  const vatZeroLabel = t(localizedTexts, "Tracker.Cart.VatZero", "VAT. 0%");

  // On a paid plan change the line price is the NEW plan's recurring price,
  // which is NOT what the member pays today — the summary card owns that number.
  // Showing it here as a line total is what made the cart look like it quoted
  // two contradictory prices, so the total is dropped and the price is dated
  // instead.
  const planChange = cart.membershipChange;
  const isPaidPlanChangeCart =
    planChange?.isPaidUser === true && !cart.gpsMaxMigration;
  const newPriceFromLabel =
    isPaidPlanChangeCart && planChange?.renewalDate
      ? t(
          localizedTexts,
          "Tracker.Cart.PlanChange.NewPriceFrom",
          "New price from {date}"
        ).replace("{date}", formatDate(planChange.renewalDate))
      : null;

  return (
    <div className={styles.container} data-component="cart-items">
      <h2 className={styles.title}>{cartTitle}</h2>

      <div className={styles.lines}>
        {groupCartLines(cart.lines).map((group) => {
          const isBundleGroup = !!group.lines[0]?.bundleId;
          const rows = group.lines.map((line) => {
            const showQuantity = !isFixedQuantityProductType(line.productType);
            // Every version of this product, so a line that already has one can
            // still be changed without removing and re-adding the item. Empty for
            // a product sold in a single version, which is most of them.
            const variants = line.availableVariants ?? [];
            const hasVariants = variants.length > 0;
            const needsVariant = line.requiresVariantSelection === true;
            const variantPromptId = `variant-required-${line.id}`;
            return (
              <div
                key={line.id}
                className={`${styles.line} ${needsVariant ? styles.lineNeedsAttention : ""}`}
                data-needs-variant={needsVariant ? "true" : undefined}
              >
                {/* Product image */}
                <div className={styles.imageWrapper}>
                  {line.image ? (
                    <img
                      // 120px square, 80px from 768px down.
                      {...fixedCmsImage(line.image.url, { width: 120 })}
                      alt={line.image.altText || line.name}
                      className={styles.image}
                      width={120}
                      height={120}
                    />
                  ) : (
                    <div className={styles.imagePlaceholder} />
                  )}
                </div>

                {/* Right content area (two rows) */}
                <div className={styles.content}>
                  {/* Top row: name/price info + line total */}
                  <div className={styles.topRow}>
                    <div className={styles.details}>
                      <div className={styles.nameRow}>
                        {/* A subscription has no product page to return to:
                            plans are chosen on the pricing page, and a recurring
                            cart holds nothing but subscriptions. */}
                        {line.productLink && !isRecurring ? (
                          <a
                            href={productHref(
                              line.productLink,
                              line.variantReference,
                              currentLocale
                            )}
                            className={styles.productName}
                          >
                            {line.name}
                          </a>
                        ) : (
                          <span className={styles.productName}>
                            {line.name}
                          </span>
                        )}
                      </div>

                      {/* Only for a product sold in ONE version — anything with
                          versions states the chosen one in its picker below, and
                          saying it twice reads as two different facts. */}
                      {!hasVariants && line.variantAttributes?.length > 0 && (
                        <div className={styles.variants}>
                          {line.variantAttributes.map((attr) => (
                            <span key={attr.key} className={styles.variant}>
                              {formatVariantAttribute(attr)}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Always offered, so a size can be changed in place rather
                          than by removing the line and adding it again. A bundle
                          added from a product card arrives with none chosen — the
                          card has no room for one picker per bundled item — and
                          only that case is dressed as an error, because only that
                          case blocks checkout. */}
                      {hasVariants && (
                        <div className={styles.variantPicker}>
                          {needsVariant && (
                            <p
                              className={styles.variantPrompt}
                              id={variantPromptId}
                              role="alert"
                            >
                              {chooseVariantRequiredLabel}
                            </p>
                          )}
                          <VariantDropdown
                            variants={variants}
                            // Empty string means no version was ever chosen, which
                            // the dropdown renders as its disabled "Select…" option.
                            selectedVariantId={line.variantReference || null}
                            onSelect={(variantId) =>
                              handleSelectVariant(line.id, variantId)
                            }
                            productName={line.name}
                            tone="cart"
                            invalid={needsVariant}
                            describedById={
                              needsVariant ? variantPromptId : undefined
                            }
                            busy={pendingVariantLineIds.has(line.id)}
                            // Only on a line that actually needs attention, so the
                            // blocked checkout doesn't scroll to a resolved picker
                            // that happens to come first.
                            selectRef={
                              line.id === firstUnresolvedLineId
                                ? firstUnresolvedRef
                                : undefined
                            }
                          />
                        </div>
                      )}

                      {line.deviceImei && (
                        <div className={styles.variants}>
                          <span className={styles.variant}>
                            {t(
                              localizedTexts,
                              "Tracker.Cart.ForDevice",
                              "For device"
                            )}
                            : {line.deviceName ?? ""}
                            {" · "}
                            {t(
                              localizedTexts,
                              "Tracker.Cart.Imei",
                              "IMEI"
                            )}{" "}
                            {maskImeiCompact(line.deviceImei)}
                          </span>
                        </div>
                      )}

                      {isB2b ? (
                        <DiscountedUnitPrice
                          variant="b2b"
                          standardInclVat={
                            line.formattedStandardUnitPriceWithVat
                          }
                          accentPrice={line.formattedUnitPrice}
                          perPiece={!isRecurring && !periodLabel}
                          periodLabel={periodLabel}
                          size="sm"
                        />
                      ) : isLineDiscounted(line) ? (
                        <DiscountedUnitPrice
                          variant="discount"
                          standardInclVat={
                            line.formattedUnitPriceWithoutAdjustments
                          }
                          accentPrice={line.formattedUnitPrice}
                          perPiece={!isRecurring && !periodLabel}
                          periodLabel={periodLabel}
                          size="sm"
                        />
                      ) : (
                        line.formattedUnitPrice && (
                          <span className={styles.unitPrice}>
                            {periodLabel
                              ? `${line.formattedUnitPrice} ${periodLabel}`
                              : isRecurring
                                ? line.formattedUnitPrice
                                : `${line.formattedUnitPrice}${perPiece}`}
                          </span>
                        )
                      )}

                      {newPriceFromLabel && (
                        <div className={styles.variants}>
                          <span className={styles.variant}>
                            {newPriceFromLabel}
                          </span>
                        </div>
                      )}

                      {/* Today's availability, read fresh with every cart load.
                          Information only — it never blocks checkout. */}
                      <StockIndicator level={line.stockLevel} surface="cart" />
                    </div>

                    <div className={styles.lineTotal}>
                      {!isPaidPlanChangeCart &&
                        (isFreeLine(line) ? (
                          // A bundle campaign zeroes the promised items. "0,00 €"
                          // reads as a pricing bug; the word does not.
                          <span className={styles.freePrice}>{freeLabel}</span>
                        ) : (
                          line.formattedTotalPrice && (
                            <span className={styles.totalPrice}>
                              {line.formattedTotalPrice}
                            </span>
                          )
                        ))}
                      {isB2b && (
                        <span className={styles.vatZeroTag}>
                          {vatZeroLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bottom row: quantity selector + delete button */}
                  <div
                    className={`${styles.bottomRow} ${showQuantity ? "" : styles.bottomRowEnd}`}
                  >
                    {showQuantity && (
                      <div className={styles.quantitySelector}>
                        <button
                          type="button"
                          className={styles.quantityBtn}
                          onClick={() =>
                            updateQuantity(
                              line.id,
                              Math.max(1, line.quantity - 1)
                            )
                          }
                          aria-label={decreaseLabel}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                        <span className={styles.quantityValue}>
                          {line.quantity}
                        </span>
                        <button
                          type="button"
                          className={styles.quantityBtn}
                          onClick={() =>
                            updateQuantity(line.id, line.quantity + 1)
                          }
                          aria-label={increaseLabel}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => handleRemoveLine(line.id)}
                      disabled={pendingDeleteLineIds.has(line.id)}
                      aria-busy={
                        pendingDeleteLineIds.has(line.id) ? "true" : undefined
                      }
                      title={line.bundleId ? removeBundleLabel : undefined}
                      aria-label={
                        line.bundleId
                          ? `${removeBundleLabel} (${line.name})`
                          : `${removeLabel} ${line.name}`
                      }
                    >
                      {pendingDeleteLineIds.has(line.id) ? (
                        <span
                          className={styles.deleteSpinner}
                          aria-hidden="true"
                        />
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          });

          if (!isBundleGroup) return rows;

          // A bundle is bought and priced as a whole, so the rows are framed
          // together and the frame says the delete takes all of them. Without
          // that, three independent-looking rows with a per-row bin icon promise
          // something the server deliberately does not do.
          return (
            <div
              key={group.key}
              className={styles.bundleGroup}
              data-component="cart-bundle-group"
            >
              <div className={styles.bundleGroupHeader}>
                <span className={styles.bundleGroupName}>
                  {group.bundleName
                    ? partOfBundleLabel.replace("{bundle}", group.bundleName)
                    : t(localizedTexts, "Tracker.Generic.Bundle", "Bundle")}
                </span>
                <span className={styles.bundleGroupHint}>
                  {removeBundleLabel}
                </span>
              </div>
              {rows}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartItems;
